/**
 * ErrorState: friendly, actionable error screen. Never a blank page or a
 * stack trace — name the problem, say what the user can do, offer retry.
 */
import { useTranslation } from 'react-i18next';

interface ErrorStateProps {
  title: string;
  message: string;
  /** Optional secondary detail (e.g. "backend unreachable"). */
  detail?: string;
  onRetry?: () => void;
}

export function ErrorState(props: ErrorStateProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className="mx-auto flex min-h-[40vh] max-w-xl flex-col items-center justify-center gap-3 text-center"
      role="alert"
    >
      <div
        aria-hidden="true"
        className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-xl font-bold text-red-700"
      >
        !
      </div>
      <h1 className="text-xl font-semibold text-ink">{props.title}</h1>
      <p className="text-sm text-stone-600">{props.message}</p>
      {props.detail && <p className="text-xs text-stone-400">{props.detail}</p>}
      {props.onRetry && (
        <button
          type="button"
          onClick={props.onRetry}
          className="mt-2 rounded-lg bg-tenant-primary px-4 py-2 text-sm font-medium text-white hover:bg-tenant-strong"
        >
          {t('common.tryAgain')}
        </button>
      )}
    </div>
  );
}
